import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Button,
  Grid,
  TablePagination,
  TableSortLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import axios from 'axios';

/** FastAPI often returns detail as string or [{ loc, msg, type }, …]; React cannot render objects. */
function formatApiError(detail) {
  if (detail == null) return 'Request failed';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (e == null) return '';
        if (typeof e === 'string') return e;
        if (typeof e === 'object' && typeof e.msg === 'string') return e.msg;
        try {
          return JSON.stringify(e);
        } catch {
          return String(e);
        }
      })
      .filter(Boolean)
      .join('; ');
  }
  if (typeof detail === 'object') {
    try {
      return JSON.stringify(detail);
    } catch {
      return 'Request failed';
    }
  }
  return String(detail);
}

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [historicalData, setHistoricalData] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('year');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Date range filters (default to last 12 months)
  const now = new Date();
  const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const [startYear, setStartYear] = useState(twelveMonthsAgo.getFullYear());
  const [startMonth, setStartMonth] = useState(twelveMonthsAgo.getMonth() + 1);
  const [endYear, setEndYear] = useState(now.getFullYear());
  const [endMonth, setEndMonth] = useState(now.getMonth() + 1);
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadHistoricalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortOrder, startYear, startMonth, endYear, endMonth]);

  const loadHistoricalData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page: page + 1, // API uses 1-based pagination
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
        start_year: startYear,
        start_month: startMonth,
        end_year: endYear,
        end_month: endMonth,
      };
      const response = await axios.get('/reports/historical-data', { params });
      setHistoricalData(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        formatApiError(detail) || err.message || 'Failed to load historical data'
      );
      console.error('Error loading historical data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(0); // Reset to first page on sort
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangePageSize = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(value);
  };

  const formatMonth = (year, month) => {
    if (year === null || month === null) return 'TOTAL';
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-AU', { year: 'numeric', month: 'long' });
  };

  const handleRefreshData = async () => {
    if (!window.confirm('This will refresh all historical data from the Bookeo API. This may take several minutes. Continue?')) {
      return;
    }
    
    try {
      setRefreshing(true);
      setError(null);
      await axios.post('/reports/refresh-data');
      setSuccess('Data refresh started. This may take several minutes. The page will refresh when complete.');
      // Reload data after a short delay
      setTimeout(() => {
        loadHistoricalData();
        setRefreshing(false);
      }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(formatApiError(detail) || err.message || 'Failed to refresh data');
      setRefreshing(false);
      console.error('Error refreshing data:', err);
    }
  };

  const exportToCSV = () => {
    if (!historicalData) return;

    const headers = [
      'Month',
      'Scheduled Tours',
      'Completed Tours',
      'Total Guests',
      'Overall Occupancy Rate (%)',
      'Total Revenue (Gross)',
      'Total Revenue (Net)',
      'Gift Voucher Sales',
    ];

    const rows = historicalData.rows.map((row) => [
      formatMonth(row.year, row.month),
      row.scheduled_tours,
      row.completed_tours,
      row.total_guests,
      Number(row.overall_occupancy_rate ?? 0).toFixed(2),
      row.total_revenue_gross || 0,
      row.total_revenue_net || 0,
      row.gift_voucher_sales || 0,
    ]);

    // Add totals row
    if (historicalData.totals) {
      rows.push([
        'TOTAL',
        historicalData.totals.scheduled_tours,
        historicalData.totals.completed_tours,
        historicalData.totals.total_guests,
        Number(historicalData.totals.overall_occupancy_rate ?? 0).toFixed(2),
        historicalData.totals.total_revenue_gross || 0,
        historicalData.totals.total_revenue_net || 0,
        historicalData.totals.gift_voucher_sales || 0,
      ]);
    }

    // Convert to CSV
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Create download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `historical_data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortDirection = (column) => {
    if (sortBy !== column) return undefined;
    return sortOrder === 'asc' ? 'asc' : 'desc';
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  // Generate year options (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  return (
    <Layout>
      <Container maxWidth={false} sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom fontWeight={700}>
              Reports
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Historical Data Report
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshData}
              disabled={refreshing || loading}
            >
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              disabled={!historicalData || loading}
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        {showFilters && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Date Range Filter
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Start Year</InputLabel>
                  <Select
                    value={startYear}
                    onChange={(e) => {
                      setStartYear(e.target.value);
                      setPage(0);
                    }}
                    label="Start Year"
                  >
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Start Month</InputLabel>
                  <Select
                    value={startMonth}
                    onChange={(e) => {
                      setStartMonth(e.target.value);
                      setPage(0);
                    }}
                    label="Start Month"
                  >
                    {months.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>End Year</InputLabel>
                  <Select
                    value={endYear}
                    onChange={(e) => {
                      setEndYear(e.target.value);
                      setPage(0);
                    }}
                    label="End Year"
                  >
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>End Month</InputLabel>
                  <Select
                    value={endMonth}
                    onChange={(e) => {
                      setEndMonth(e.target.value);
                      setPage(0);
                    }}
                    label="End Month"
                  >
                    {months.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {!loading && historicalData && (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.main', '& th': { color: 'primary.contrastText' } }}>
                    <TableCell sx={{ color: 'primary.contrastText', fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === 'year'}
                        direction={getSortDirection('year')}
                        onClick={() => handleSort('year')}
                        sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                      >
                        Month
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === 'scheduled_tours'}
                        direction={getSortDirection('scheduled_tours')}
                        onClick={() => handleSort('scheduled_tours')}
                        sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                      >
                        Scheduled Tours
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === 'completed_tours'}
                        direction={getSortDirection('completed_tours')}
                        onClick={() => handleSort('completed_tours')}
                        sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                      >
                        Completed Tours
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === 'total_guests'}
                        direction={getSortDirection('total_guests')}
                        onClick={() => handleSort('total_guests')}
                        sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                      >
                        Total Guests
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === 'overall_occupancy_rate'}
                        direction={getSortDirection('overall_occupancy_rate')}
                        onClick={() => handleSort('overall_occupancy_rate')}
                        sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                      >
                        Overall Occupancy Rate
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === 'total_revenue_gross'}
                        direction={getSortDirection('total_revenue_gross')}
                        onClick={() => handleSort('total_revenue_gross')}
                        sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                      >
                        Total Revenue (Gross)
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === 'total_revenue_net'}
                        direction={getSortDirection('total_revenue_net')}
                        onClick={() => handleSort('total_revenue_net')}
                        sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                      >
                        Total Revenue (Net)
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === 'gift_voucher_sales'}
                        direction={getSortDirection('gift_voucher_sales')}
                        onClick={() => handleSort('gift_voucher_sales')}
                        sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                      >
                        Gift Voucher Sales
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(historicalData.rows ?? []).map((row) => (
                    <TableRow
                      key={`${row.year}-${row.month}`}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                        '&:hover': { backgroundColor: 'action.selected' },
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {formatMonth(row.year, row.month)}
                      </TableCell>
                      <TableCell align="right">{row.scheduled_tours}</TableCell>
                      <TableCell align="right">{row.completed_tours}</TableCell>
                      <TableCell align="right">{row.total_guests}</TableCell>
                      <TableCell align="right">
                        {row.overall_occupancy_rate !== null && row.overall_occupancy_rate !== undefined
                          ? `${Number(row.overall_occupancy_rate).toFixed(2)}%`
                          : '0.00%'}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.total_revenue_gross)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.total_revenue_net)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.gift_voucher_sales)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totals Row */}
                  {historicalData.totals && (
                    <TableRow
                      sx={{
                        backgroundColor: 'primary.light',
                        '& td': { fontWeight: 'bold', color: 'white' },
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {formatMonth(historicalData.totals.year, historicalData.totals.month)}
                      </TableCell>
                      <TableCell align="right">{historicalData.totals.scheduled_tours}</TableCell>
                      <TableCell align="right">{historicalData.totals.completed_tours}</TableCell>
                      <TableCell align="right">{historicalData.totals.total_guests}</TableCell>
                      <TableCell align="right">
                        {historicalData.totals.overall_occupancy_rate !== null &&
                        historicalData.totals.overall_occupancy_rate !== undefined
                          ? `${Number(historicalData.totals.overall_occupancy_rate).toFixed(2)}%`
                          : '0.00%'}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(historicalData.totals.total_revenue_gross)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(historicalData.totals.total_revenue_net)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(historicalData.totals.gift_voucher_sales)}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={historicalData.total_count || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangePageSize}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />

            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label={`${historicalData.total_count || 0} months of data`}
                color="primary"
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                * Overall Occupancy Rate = Total Guests / (Completed Tours × 12) × 100
              </Typography>
            </Box>
          </>
        )}
      </Container>
    </Layout>
  );
};

export default Reports;
